import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { BehaviorSubject, combineLatest, map, startWith, Subject, switchMap, take, tap, withLatestFrom, } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { IBaseSearchRequest, IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { ICompany } from 'src/app/company/models/company';
import { DeviceStatusEnum} from '../models/device';
import { ConnectivityStateService } from 'src/app/shared/services/connectivity-state.service';
import * as DeviceActions from "../../stores/global/devices/device-actions";
import * as CompanyActions from "../../stores/global/companies/company-actions";
import * as DeviceCategoryActions from "../../stores/global/device-categories/actions";
import * as DeviceLocationsActions from "../../stores/global/device-locations/device-locations-actions";
import * as InstallationsActions from "../../stores/global/installations/installation-actions";
import * as DeviceManufacturerActions from "../../stores/global/device-manufacturers/device-manufacturers-actions";
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { deviceCategoriesSelector } from 'src/app/stores/global/device-categories/selectors';
import { deviceLocationSelector } from 'src/app/stores/global/device-locations/device-location-selectors';
import { deviceManufacturerSelector } from 'src/app/stores/global/device-manufacturers/device-manufacturers-selectors';
import { DeviceCategoryStore } from 'src/app/stores/component/device-category-store';
import { DeviceService } from '../services/device.service';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { DeviceExportStore } from 'src/app/stores/component/device-export-store';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { isLoadingImportSelector } from 'src/app/stores/global/devices/device-selectors';
import { installationsSelector } from 'src/app/stores/global/installations/installation-selectors';
import { IGetInstallationsByCompanyRequest } from 'src/app/installations/models/installation';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { endOfLocalDay } from 'src/app/shared/helpers/formatDates';


@UntilDestroy()
@Component({
    selector: 'app-device-view-admin',
    templateUrl: './device-view-admin.component.html',
    styleUrl: './device-view-admin.component.scss',
    providers: [DeviceCategoryStore, DeviceExportStore],
    standalone: false
})
export class DeviceViewAdminComponent implements OnInit {
  @Input() showActionCol : boolean = true;
  @Input() showOrderColumn : boolean = true;
  @Input() createProducts : boolean = true;
  @Input() reloadResults : boolean = true;
  @Input() loadAllProducts : boolean = true;
  @Input() isAdminView : boolean = false;
  @Input() currentCompany: ICompany = null;

  constructor(
    private fb : UntypedFormBuilder,
    private connectivityStateService : ConnectivityStateService,
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private router: Router,
    private deviceService: DeviceService,
    private deviceExportStore: DeviceExportStore,
  ) {
  }


  ////////////////
  // Variables //
  //////////////

  selectedFile: File | null = null;

  importForm: FormGroup = new FormGroup({
    file: new FormControl(null, Validators.required)
  });

  showUpload: boolean = false;
  loading: boolean = false;

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: "contains",
  };


  // Grid Form
  // TODO: is still needed?
  public formGroup = new UntypedFormGroup({
    companyId: new UntypedFormControl(''),
    manufacturerId: new UntypedFormControl(''),
    serialNumber: new UntypedFormControl(''),
    deviceStatus: new UntypedFormControl(''),
  });


  // Create filter form
  filterForm = this.fb.group({
    keyword: new FormControl(null),
    deviceCategoryId: new UntypedFormControl(null),
    deviceManufacturerId: new UntypedFormControl(null),
    installationId: new UntypedFormControl(null),
    companyId: new UntypedFormControl(null),
    deviceStatus: new UntypedFormControl(''),
    deviceLocationId: new UntypedFormControl(null),
    executionDateFrom: new UntypedFormControl(null),
    executionDateTo: new UntypedFormControl(null),
    productKeyword: ['']
  });

  deviceStatusArray = enumToArray(DeviceStatusEnum, "global.deviceStatusEnums.");

  ///////////////
  // Getters  //
  /////////////

  get pageSizes() {
    return PageSize;
  }

  get deviceStatus(){
    return DeviceStatusEnum;
  }

  getControl(path: string) {
    return this.filterForm.get(path) as FormControl;
  }


  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseSearchRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();

  vm$ = combineLatest([
    this.gridState$.pipe(),
    this.deviceExportStore.loading$,
    this.searchRequest$.pipe(
      switchMap((request) => {
        this.loading = true;
        return this.deviceService.getDevices(request)
      }),
      tap(() => {
        this.loading = false; // Stop loading when data is fetched
      }),
      map(x => {
        var gridData : GridDataResult = {
          data: x.data,
          total: x.totalCount
        }
        return gridData
      }),
      startWith(null)
    ),
    this.searchRequest$.pipe(startWith(null)),
    this.pageChangeSub$.pipe(
      withLatestFrom(this.searchRequest$),
      map(([pageChangeEvent, searchRequest]) => ({
        pageChangeEvent,
        searchRequest,
      })),
      tap((x) => {
        const pageNumber = Math.round(
          (x.pageChangeEvent.skip + x.pageChangeEvent.take) /
            x.pageChangeEvent.take
        );
 
        this.gridState$.next({
          skip: x.pageChangeEvent.skip,
          pageSize: x.pageChangeEvent.take,
        });
 
        this.searchRequest$.next({
          ...x.searchRequest,
          pageNumber: pageNumber,
          pageSize: x.pageChangeEvent.take,
        });
      }),
      startWith({ skip: 0, take: 10 })
    ),
    this.store.pipe(select(isLoadingImportSelector)),
    this.store.pipe(select(companySelector)),
    this.store.pipe(select(deviceCategoriesSelector)),
    this.store.pipe(select(installationsSelector)),
    this.store.pipe(select(deviceLocationSelector)),
    this.store.pipe(select(deviceManufacturerSelector)),
    this.connectivityStateService.connectionState$,
    this.filterForm.get('companyId').valueChanges.pipe(
      withLatestFrom(this.searchRequest$),
      map(([companyId, searchRequest]) => ({companyId, searchRequest})),
      tap(data => {
        if(data.companyId != null && data.companyId != data.searchRequest.advancedFilter?.filters?.find(x => x.field == "company.id")?.value){
          // reset deviceLocation field if company changed
          this.filterForm.get('deviceLocationId')?.patchValue(null,{emitEvent: false});
          this.filterForm.get('installationId')?.patchValue(null,{emitEvent: false});

          this.store.dispatch(DeviceLocationsActions.getDeviceLocationsByCompany({companyId: data.companyId}));

          var request : IGetInstallationsByCompanyRequest = {
            companyId: data.companyId
          };
          this.store.dispatch(InstallationsActions.getInstallationsByCompany({request}))
        }

        if(data.companyId == null){
          this.filterForm.get('deviceLocationId')?.disable({emitEvent: false});
          this.filterForm.get('installationId')?.disable({emitEvent: false});
        }
        else {
          this.filterForm.get('deviceLocationId')?.enable({emitEvent: false});
          this.filterForm.get('installationId')?.enable({emitEvent: false});
        }
      }),
      startWith(null),
    ),
    this.route.queryParams.pipe(
      tap(params => this.patchFilterForm(params)),
      withLatestFrom(this.searchRequest$),
      map(([params, searchRequest]) => ({params, searchRequest})),
      tap(data => {
        if(JSON.stringify(data.params) !== "{}")
        {
          this.onSubmit(data.searchRequest)
        }
      }),
      take(1) // only execute once
    )
  ]).pipe(
    map(
      ([
        gridState,
        isExportLoading,
        devices,
        searchRequest,
        pageChangeSub,
        isLoadingImport,
        companies,
        deviceCategories,
        installations,
        deviceLocations,
        deviceManufacturers,
        connectionState,
        filterForm   
      ]) => ({
        gridState,
        isExportLoading,
        devices,
        searchRequest,
        pageChangeSub,
        isLoadingImport,
        companies,
        deviceCategories,
        installations,
        deviceLocations,
        deviceManufacturers,
        connectionState,
        filterForm
      })
    )
  );


  ngOnInit(): void {
    this.store.dispatch(CompanyActions.getCompanies());
    this.store.dispatch(DeviceCategoryActions.getDeviceCategories());
    this.store.dispatch(DeviceManufacturerActions.getDeviceManufacturers());
  }

  ////////////////
  // functions //
  //////////////

  // loadData() {

  patchFilterForm(params: any) {
    this.filterForm.patchValue({
      keyword: params['keyword'] || null,
      deviceManufacturerId: params['deviceManufacturerId'] || null,
      companyId: params['companyId'] || null,
      deviceStatus: parseInt(params['deviceStatus']) || null,
      deviceCategoryId: params['deviceCategoryId'] || null,
      deviceLocationId: params['deviceLocationId'] || null,
      installationId: params['installationId'] || null,
      executionDateFrom: params['executionDateFrom'] != null ? new Date(params['executionDateFrom']) : null,
      executionDateTo: params['executionDateTo'] != null ? new Date(params['executionDateTo']) : null,
      productKeyword: params['productKeyword'] || null
    });
  }

  onSubmit(request: IBaseSearchRequest){
    var formValues = this.filterForm.value;

    const searchRequest: IBaseSearchRequest = {
      ...request,
      advancedFilter: undefined,
      productKeyword: undefined,
      scheduledDateFrom: undefined,
      scheduledDateTo: undefined,
    };

    // Trim the keyword before using
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;

    // Fields to search for the keyword
    const searchFields = ['serialNumber', 'tagNumber', 'equipmentNumber', 'type', 'qrNumber'];

    // Create keyword filters
    const keywordFilters: IFilter[] = keyword 
        ? searchFields.map(field => ({ field, operator: 'contains', value: keyword })) 
        : [];

    // Other filters
    var filterObject : IFilter = {
      logic: "and",
      filters: []
    }

    var otherFilters: IFilter[] =[]

    if(formValues.companyId) {
      otherFilters.push({ field: 'company.id', operator: 'eq', value: formValues.companyId })
    }

    if(formValues.deviceLocationId) {
      otherFilters.push({ field: 'deviceLocation.id', operator: 'eq', value: formValues.deviceLocationId })
    }

    if(formValues.installationId) {
      otherFilters.push({ field: 'installation.id', operator: 'eq', value: formValues.installationId })
    }

    if(formValues.deviceManufacturerId) {
      otherFilters.push({ field: 'manufacturer.id', operator: 'eq', value: formValues.deviceManufacturerId })
    }

    if(formValues.deviceCategoryId) {
      otherFilters.push({ field: 'deviceCategory.id', operator: 'eq', value: formValues.deviceCategoryId })
    }

    if(formValues.deviceStatus) {
      otherFilters.push({ field: 'deviceStatus', operator: 'eq', value: formValues.deviceStatus ? formValues.deviceStatus.toString() : null })
    }

    // Product keyword search - passed as dedicated property
    if(formValues.productKeyword && formValues.productKeyword.trim()) {
      searchRequest.productKeyword = formValues.productKeyword.trim();
    }

    // Combine keyword filters with 'or' logic if there are any
    const combinedKeywordFilter: IFilter = { logic: 'or', filters: keywordFilters } 

    // Combine keyword filters with other filters using 'and' logic
    const allFilters: IFilter[] = combinedKeywordFilter.filters.length > 0
        ? [combinedKeywordFilter, ...otherFilters]
        : otherFilters;

    if(formValues.executionDateFrom){
      formValues.executionDateFrom = new Date(formValues.executionDateFrom).toISOString();
      searchRequest.scheduledDateFrom = formValues.executionDateFrom;
    }
    if(formValues.executionDateTo){
      formValues.executionDateTo = new Date(formValues.executionDateTo).toISOString();
      searchRequest.scheduledDateTo = endOfLocalDay(formValues.executionDateTo);
    }

    filterObject.filters = allFilters;

    if(filterObject.filters.length > 0){
      searchRequest.advancedFilter = filterObject;
    }

    this.searchRequest$.next(searchRequest);

    this.updateUrlWithQuery(formValues);
  }

  updateUrlWithQuery(formValues){
    // Update URL with the filter parameters
    const queryParams = {
      keyword: formValues.keyword || null,
      deviceCategoryId: formValues.deviceCategoryId || null,
      deviceManufacturerId: formValues.deviceManufacturerId || null,
      installationId: formValues.installationId || null,
      companyId: formValues.companyId || null,
      deviceStatus: formValues.deviceStatus || null,
      deviceLocationId: formValues.deviceLocationId || null,
      executionDateFrom: formValues.executionDateFrom || null,
      executionDateTo: formValues.executionDateTo || null,
      productKeyword: formValues.productKeyword || null
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  resetFilter(): void {
    // reset form
    this.filterForm.reset();
    
    this.searchRequest$.next({pageNumber: 0,pageSize: this.pageSizes.Small})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    // reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }

  showImportForm(): void {
    this.showUpload = true;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      document.getElementById("file-upload-label").innerHTML = this.selectedFile.name;
      this.importForm.patchValue({ file: this.selectedFile });
    }
  }

  onUpload(): void {
    if (this.importForm.invalid) return;

    const formData = new FormData();
    formData.append('file', this.importForm.get('file')?.value);

    this.store.dispatch(DeviceActions.importDevices({formData: formData}));

    this.cancelUpload();
  }
  
  cancelUpload() {
    this.showUpload = false;
    this.importForm.reset();
  }

  exportDevices(searchRequest : IBaseSearchRequest){
    this.deviceExportStore.exportDevices(searchRequest);
  }
}
